import { lazyWithRetry } from "@/utils/lazy-with-retry";
import { Navigate, useLocation, type RouteObject } from "react-router-dom";
import { RequireAuth } from "@/routes/guards/RequireAuth";
import { RequireActive } from "@/routes/guards/RequireActive";
import {
  ROOT,
  LANDING_PAGE,
  LOGIN,
  SIGNUP,
  INVITE_LANDING,
  MOBILE_APP_LANDING,
  GETTING_STARTED_SETUP,
  ACTIVATE,
  ONBOARDING,
  TERMS,
  PRIVACY,
  CHATGPT_CONNECT,
  CHATGPT_CONNECTIONS,
  FAQ,
  HOME,
  STASHT_CHAT,
  CHAT,
  HELP,
  RESET,
  RESET_PASSWORD,
  VERIFY_EMAIL,
  UNSUBSCRIBE,
  DECK,
  BLOG,
  BLOG_POST,
  BLOG_CATEGORY,
  APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS,
  SAVE_PLACES_FROM_TIKTOK,
  ORGANIZE_SAVED_REELS,
  SAVE_TIKTOKS,
  SAVE_RECIPES_FROM_INSTAGRAM_TIKTOK,
  SAVE_TRAVEL_IDEAS_FROM_INSTAGRAM_TIKTOK,
  SAVE_EVENTS_FROM_INSTAGRAM_TIKTOK,
  SAVE_GIFT_IDEAS_FROM_SOCIAL_MEDIA,
  RECIME_ALTERNATIVE,
  STACKLIST_ALTERNATIVE,
  COMPARE,
  DEBUG,
  QR_LOGIN_SCANNER,
  QR_LOGIN_CONFIRM,
  QR_SCANNER,
  INVITE_QR,
} from "@/routes";
import AppShell from "@/layouts/AppShell";
import { startPagePath } from "@/features/navigation/pages";
import PageWrapper from "@/layouts/PageWrapper";
import PageAuthLoader from "@/components/PageAuthLoader";
import { APP_SHELL_ROUTES } from "@/routes/appShellRouteRegistry";
import WebLanding from "@/pages/WebLanding";
import { isNativeApp } from "@/utils/platform-detection";
import { RequireDevelopment } from "@/routes/guards/RequireDevelopment";
import { useAuth } from "@/contexts/AuthContext";

export {
  STACK_ROUTES,
  TAB_ID_TO_PATH,
  TAB_ROUTES,
  getTabIdFromPath,
  getTabReselectAction,
  getTabRouteById,
  getTabRouteByPath,
} from "@/routes/appShellRouteRegistry";
export type {
  ReselectAction,
  RouteType,
  StackedRouteEntry,
  StackedRouteHandle,
  TabId,
  TabRouteEntry,
} from "@/routes/appShellRouteTypes";

const NullRoute = () => null;

// 404 page
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));

// Auth pages
const AuthPage = lazyWithRetry(() => import("@/pages/AuthPage"));
const ChatGPTConnection = lazyWithRetry(() => import("@/pages/ChatGPTConnection"));
const ResetPasswordPage = lazyWithRetry(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazyWithRetry(() => import("@/pages/VerifyEmailPage"));
const MobileAppLanding = lazyWithRetry(() => import("@/pages/MobileAppLanding"));

// Other public pages
const SetupStasht = lazyWithRetry(() => import("@/pages/SetupStasht"));
const ActivationGate = lazyWithRetry(() => import("@/pages/ActivationGate"));
const Terms = lazyWithRetry(() => import("@/pages/Terms"));
const Privacy = lazyWithRetry(() => import("@/pages/Privacy"));
const FAQPage = lazyWithRetry(() => import("@/pages/FAQ"));
const Unsubscribe = lazyWithRetry(() => import("@/pages/Unsubscribe"));
const DeckRoom = lazyWithRetry(() => import("@/pages/DeckRoom"));

// Marketing / content pages (public, prerendered at build time)
const BlogIndex = lazyWithRetry(() => import("@/pages/blog/BlogIndex"));
const BlogPostPage = lazyWithRetry(() => import("@/pages/blog/BlogPostPage"));
const BlogCategoryPage = lazyWithRetry(() => import("@/pages/blog/BlogCategoryPage"));
const UseCasePage = lazyWithRetry(() => import("@/pages/marketing/UseCasePage"));
const ComparePage = lazyWithRetry(() => import("@/pages/marketing/ComparePage"));

const Help = lazyWithRetry(() => import("@/pages/Help"));
const Reset = lazyWithRetry(() => import("@/pages/Reset"));
const QrScannerPage = lazyWithRetry(() => import("@/pages/QrScannerPage"));
const QrLoginConfirmPage = lazyWithRetry(() => import("@/pages/QrLoginConfirmPage"));
const InviteQrPage = lazyWithRetry(() => import("@/pages/InviteQrPage"));

const InviteLanding = lazyWithRetry(() => import("@/pages/InviteLanding"));

// Lazy load dev pages
const Debug = lazyWithRetry(() => import("@/pages/Debug"));

/**
 * Read the invite code from the query string, if present.
 * Used by entry points that need to upgrade `?invite=CODE` URLs to the
 * canonical invite landing route at `/i/:code`.
 */
const useInviteCodeFromSearch = (): string | null => {
  const location = useLocation();
  const code = new URLSearchParams(location.search).get("invite")?.trim();
  return code && code.length > 0 ? code : null;
};

/**
 * Redirect /signup to /login by default. If `?invite=CODE` is present,
 * route to the invite landing page instead so invitees get the richer FTUE.
 */
const SignupRedirect = () => {
  const location = useLocation();
  const inviteCode = useInviteCodeFromSearch();
  if (inviteCode) {
    return <Navigate to={`/i/${encodeURIComponent(inviteCode)}`} replace />;
  }
  return <Navigate to={{ pathname: LOGIN(), search: location.search }} replace />;
};

/**
 * Login route with invite-aware redirect. If the URL includes `?invite=CODE`,
 * send the user to the invite landing page rather than dropping them on the
 * login form.
 */
const LoginRoute = () => {
  const inviteCode = useInviteCodeFromSearch();
  if (inviteCode) {
    return <Navigate to={`/i/${encodeURIComponent(inviteCode)}`} replace />;
  }
  return (
    <PageWrapper>
      <AuthPage />
    </PageWrapper>
  );
};

const RootEntry = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const search = location.search;
  const inviteCode = useInviteCodeFromSearch();

  if (isLoading) {
    return <PageAuthLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to={{ pathname: startPagePath(), search }} replace />;
  }

  // Unauthenticated invitees get routed to the invite landing page.
  if (inviteCode) {
    return <Navigate to={`/i/${encodeURIComponent(inviteCode)}`} replace />;
  }

  // Native apps (iOS/Android) show mobile app landing
  if (isNativeApp()) {
    return (
      <PageWrapper>
        <MobileAppLanding />
      </PageWrapper>
    );
  }

  // Web browsers show web landing
  return (
    <PageWrapper>
      <WebLanding />
    </PageWrapper>
  );
};

export const APP_ROUTES: RouteObject[] = [
  { path: ROOT(), element: <RootEntry /> },
  { path: CHATGPT_CONNECT(), element: <ChatGPTConnection /> },
  { path: CHATGPT_CONNECTIONS(), element: <ChatGPTConnection /> },
  { path: "/feed", element: <Navigate to={HOME()} replace /> },
  {
    path: LANDING_PAGE(),
    element: (
      <PageWrapper>
        <WebLanding />
      </PageWrapper>
    ),
  },
  {
    path: LOGIN(),
    element: <LoginRoute />,
  },
  {
    path: SIGNUP(),
    element: <SignupRedirect />,
  },
  {
    path: INVITE_LANDING(),
    element: (
      <PageWrapper>
        <InviteLanding />
      </PageWrapper>
    ),
  },
  {
    path: MOBILE_APP_LANDING(),
    element: (
      <PageWrapper>
        <MobileAppLanding />
      </PageWrapper>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      {
        path: GETTING_STARTED_SETUP(),
        element: (
          <PageWrapper>
            <SetupStasht />
          </PageWrapper>
        ),
      },
      {
        path: ACTIVATE(),
        element: <Navigate to={HOME()} replace />,
      },
      {
        // Absorbed by the universal scanner; alias kept so existing entry
        // points and muscle memory keep working.
        path: QR_LOGIN_SCANNER(),
        element: <Navigate to={QR_SCANNER()} replace />,
      },
      {
        path: QR_SCANNER(),
        element: (
          <PageWrapper>
            <QrScannerPage />
          </PageWrapper>
        ),
      },
      {
        path: INVITE_QR(),
        element: (
          <PageWrapper>
            <InviteQrPage />
          </PageWrapper>
        ),
      },
      {
        path: QR_LOGIN_CONFIRM(),
        element: (
          <PageWrapper>
            <QrLoginConfirmPage />
          </PageWrapper>
        ),
      },
      {
        path: ONBOARDING(),
        element: (
          <PageWrapper>
            <ActivationGate />
          </PageWrapper>
        ),
      },
    ],
  },
  {
    path: TERMS(),
    element: (
      <PageWrapper>
        <Terms />
      </PageWrapper>
    ),
  },
  {
    path: PRIVACY(),
    element: (
      <PageWrapper>
        <Privacy />
      </PageWrapper>
    ),
  },
  {
    path: FAQ(),
    element: (
      <PageWrapper>
        <FAQPage />
      </PageWrapper>
    ),
  },
  {
    path: BLOG(),
    element: (
      <PageWrapper>
        <BlogIndex />
      </PageWrapper>
    ),
  },
  {
    path: BLOG_CATEGORY(),
    element: (
      <PageWrapper>
        <BlogCategoryPage />
      </PageWrapper>
    ),
  },
  {
    path: BLOG_POST(),
    element: (
      <PageWrapper>
        <BlogPostPage />
      </PageWrapper>
    ),
  },
  ...[
    APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS(),
    SAVE_PLACES_FROM_TIKTOK(),
    ORGANIZE_SAVED_REELS(),
    SAVE_TIKTOKS(),
    SAVE_RECIPES_FROM_INSTAGRAM_TIKTOK(),
    SAVE_TRAVEL_IDEAS_FROM_INSTAGRAM_TIKTOK(),
    SAVE_EVENTS_FROM_INSTAGRAM_TIKTOK(),
    SAVE_GIFT_IDEAS_FROM_SOCIAL_MEDIA(),
    RECIME_ALTERNATIVE(),
    STACKLIST_ALTERNATIVE(),
  ].map(path => ({
    path,
    element: (
      <PageWrapper>
        <UseCasePage />
      </PageWrapper>
    ),
  })),
  {
    path: COMPARE(),
    element: (
      <PageWrapper>
        <ComparePage />
      </PageWrapper>
    ),
  },
  {
    path: RESET(),
    element: (
      <PageWrapper>
        <Reset />
      </PageWrapper>
    ),
  },
  {
    path: RESET_PASSWORD(),
    element: (
      <PageWrapper>
        <ResetPasswordPage />
      </PageWrapper>
    ),
  },
  {
    path: VERIFY_EMAIL(),
    element: (
      <PageWrapper>
        <VerifyEmailPage />
      </PageWrapper>
    ),
  },
  {
    path: UNSUBSCRIBE(),
    element: (
      <PageWrapper>
        <Unsubscribe />
      </PageWrapper>
    ),
  },
  {
    path: DECK(),
    element: (
      <PageWrapper>
        <DeckRoom />
      </PageWrapper>
    ),
  },
  // /stash/:id and /stuff/:id live inside the authenticated AppShell so they render
  // as stack overlays (preserving tab scroll position). RequireAuth allows these
  // public content paths through without auth so anonymous users can still view them.
  // Chat was tried out here before it became the Chat tab; old links land there.
  { path: STASHT_CHAT(), element: <Navigate to={CHAT()} replace /> },
  {
    element: (
      <RequireAuth>
        <RequireActive>
          <AppShell />
        </RequireActive>
      </RequireAuth>
    ),
    children: APP_SHELL_ROUTES.map(route => ({
      path: route.path,
      element: <NullRoute />,
      handle: route.handle,
    })),
  },
  {
    element: <RequireAuth />,
    children: [
      {
        path: HELP(),
        element: (
          <PageWrapper>
            <Help />
          </PageWrapper>
        ),
      },
    ],
  },
  {
    element: (
      <RequireDevelopment>
        <RequireAuth />
      </RequireDevelopment>
    ),
    children: [
      {
        path: DEBUG(),
        element: (
          <PageWrapper>
            <Debug />
          </PageWrapper>
        ),
      },
    ],
  },
  {
    path: "*",
    element: (
      <PageWrapper>
        <NotFound />
      </PageWrapper>
    ),
  },
];
